import React from "react";

const ROWS = [
  ["Forecast basis", "Manual rep/manager guess or flat stage probability", "Weighted by YOUR historical stage win-rates"],
  ["Forecast history", "Not kept — no week-over-week tracking", "Append-only snapshots, full week-over-week delta"],
  ["Why the number moved", "Not explained", "Named deals + reasons, every Monday"],
  ["Deals going stale", "No automatic flagging", "Risk Radar with suggested actions"],
  ["Funnel leak analysis", "Basic reports", "Stage conversion, cycle time, win rate by source"],
  ["Delivery", "You go build/check a dashboard", "AI briefing lands in your inbox Monday 06:00"],
  ["Setup", "It's already there, but you configure reports yourself", "Connect in 10 min, opinionated defaults"],
  ["Cost", "Included in HubSpot", "From €99/mo — adds the analysis HubSpot doesn't"],
];

export default function VsHubSpotPage() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = "Recrysta vs HubSpot Forecast — Why your CRM forecast falls short";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "HubSpot's built-in forecast is a manager guess. Recrysta computes the real weighted forecast from your own win-rate history and emails it every Monday.");
    const canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    canonical?.setAttribute("href", "https://recrysta.com/vs-hubspot");
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "vs-hs-jsonld";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Recrysta vs HubSpot Forecast",
      url: "https://recrysta.com/vs-hubspot",
      description: "Comparison between HubSpot's native forecast and Recrysta's weighted-forecast AI briefing.",
    });
    document.head.appendChild(s);
    return () => {
      document.title = prevTitle;
      if (prevDesc) desc?.setAttribute("content", prevDesc);
      if (prev) canonical?.setAttribute("href", prev);
      document.getElementById("vs-hs-jsonld")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="vs-hubspot-page">
      <header className="border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-4">
          <a href="/" className="font-bold text-sm">← Recrysta</a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
          <span className="text-emerald-500/70">/</span>vs_hubspot
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
          HubSpot tells you the pipeline. <span className="text-emerald-600">Recrysta tells you what you&rsquo;ll close.</span>
        </h1>
        <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl">
          HubSpot is an excellent CRM — probably the reason we integrate with it first. But its
          built-in forecast has known limits for small teams: no forecast history, probabilities that
          are either a manager guess or a flat stage default, no automatic risk flagging, and no
          weekly narrative that explains what actually moved the number.
        </p>

        <div className="mt-10 overflow-x-auto rounded-2xl ring-1 ring-slate-200">
          <table className="w-full text-sm border-separate border-spacing-0" data-testid="vs-hubspot-table">
            <thead>
              <tr>
                <th className="text-left p-4 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500 bg-white w-1/4">Feature</th>
                <th className="text-left p-4 border-b border-slate-200 font-bold text-slate-700 bg-slate-50">HubSpot Forecast</th>
                <th className="text-left p-4 border-b-2 border-emerald-400 font-bold text-white bg-slate-950 relative">
                  <span className="inline-flex items-center gap-2">
                    Recrysta
                    <span className="rounded-full bg-emerald-500 text-slate-950 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5">
                      Winner
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([f, h, r], i) => {
                const last = i === ROWS.length - 1;
                return (
                  <tr key={i}>
                    <td className={`p-4 font-semibold text-slate-700 align-top bg-white ${last ? "" : "border-b border-slate-100"}`}>{f}</td>
                    <td className={`p-4 text-slate-600 align-top bg-slate-50/60 ${last ? "" : "border-b border-slate-100"}`}>{h}</td>
                    <td className={`p-4 text-slate-100 align-top bg-slate-950 ${last ? "" : "border-b border-slate-800"}`}>
                      <span className="inline-flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
                        <span>{r}</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-200 p-8">
          <p className="text-slate-700 leading-relaxed">
            <strong>Recrysta doesn&rsquo;t replace HubSpot</strong> — it reads your HubSpot data and adds the
            analyst layer on top. Read-only OAuth, 10-minute setup, nothing written back to your CRM.
          </p>
        </div>

        <div className="mt-10 text-center">
          <a href="/signup" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 text-slate-950 px-5 py-3 text-sm font-bold hover:bg-emerald-400 transition-colors" data-testid="vs-hubspot-cta">
            Start free 14-day trial →
          </a>
          <div className="mt-3 text-xs text-slate-500">No credit card required.</div>
        </div>
      </main>
      <footer className="border-t border-slate-200 bg-slate-50 py-6">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-xs text-slate-500 flex gap-6 flex-wrap">
          <span>© Recrysta {new Date().getFullYear()}</span>
          <a href="/privacy" className="hover:text-slate-900">Privacy</a>
          <a href="/imprint" className="hover:text-slate-900">Imprint</a>
          <a href="/faq" className="hover:text-slate-900">FAQ</a>
          <a href="/vs-hubspot" className="hover:text-slate-900">Recrysta vs HubSpot</a>
        </div>
      </footer>
    </div>
  );
}
